<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTenant } from '@/composables/useTenant';
import { Building2, ChevronDown, Check, ShieldCheck } from 'lucide-vue-next';

const { t } = useI18n();
const { activeTenant, availableTenants, setActiveTenant } = useTenant();
const isOpen = ref(false);

const tenantTypeDisplay = computed(() => {
  const tier = activeTenant.value.tier || activeTenant.value.type;
  if (tier === 'PRO') return { label: '都市対抗 (社会人)', color: 'text-emerald-400' };
  if (tier === 'COLLEGIATE') return { label: '大学野球 (六大学)', color: 'text-indigo-400' };
  if (tier === 'ACADEMIC') return { label: 'リトルリーグ (12U)', color: 'text-amber-400' };
  if (tier === 'AMATEUR') return { label: 'リトルシニア (15U)', color: 'text-sky-400' };
  return { label: 'その他連盟', color: 'text-purple-400' };
});

const getTierLabel = (tier?: string) => {
  if (tier === 'PRO') return '都市対抗';
  if (tier === 'COLLEGIATE') return '大学野球';
  if (tier === 'ACADEMIC') return 'リトルリーグ';
  if (tier === 'AMATEUR') return 'リトルシニア';
  return tier || '一般';
};

const selectTenant = async (tenantId: string) => {
  await setActiveTenant(tenantId);
  isOpen.value = false;
};
</script>

<template>
  <div class="relative">
    <button
      type="button"
      @click="isOpen = !isOpen"
      class="flex items-center gap-2.5 px-3 py-1.5 rounded-xl bg-slate-950 border border-slate-800 hover:border-slate-700 transition duration-150 group shadow-sm text-left cursor-pointer"
      :title="`選択中: ${activeTenant.name}`"
    >
      <div class="w-7 h-7 rounded-lg bg-sky-500/15 border border-sky-500/30 flex items-center justify-center text-sky-400 group-hover:scale-105 transition-transform shrink-0">
        <Building2 class="w-4 h-4" />
      </div>

      <div class="leading-tight">
        <div class="flex items-center gap-1.5">
          <span class="text-xs font-bold text-slate-100 truncate max-w-[140px] sm:max-w-[220px]">
            {{ activeTenant.name }}
          </span>
          <ChevronDown class="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-200 transition-transform" :class="{ 'rotate-180': isOpen }" />
        </div>
        <div class="flex items-center gap-1.5">
          <span :class="['text-[9px] uppercase tracking-wider font-black', tenantTypeDisplay.color]">
            {{ tenantTypeDisplay.label }}
          </span>
          <span class="text-[9px] text-slate-500 font-mono">&bull; {{ activeTenant.seasonYear || '2025' }}</span>
        </div>
      </div>
    </button>

    <!-- Dropdown to switch Japanese Leagues & Tenants -->
    <div
      v-if="isOpen"
      class="absolute left-0 mt-2 w-80 bg-slate-900 border border-slate-700/80 rounded-2xl shadow-2xl p-2 z-50 animate-in fade-in zoom-in-95 duration-100 space-y-1"
      style="background-color: #0f172a;"
    >
      <div class="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-800 flex items-center justify-between">
        <span>所属組織・リーグ切り替え</span>
        <span class="text-[9px] text-sky-400 font-mono">{{ availableTenants.length }} 組織</span>
      </div>

      <div class="max-h-72 overflow-y-auto space-y-1">
        <button
          v-for="item in availableTenants"
          :key="item.id"
          type="button"
          @click="selectTenant(item.id)"
          :class="[
            'w-full flex items-center justify-between p-2.5 rounded-xl text-left transition',
            item.id === activeTenant.id
              ? 'bg-sky-500/15 border border-sky-500/40 text-white shadow-sm'
              : 'hover:bg-slate-800/80 text-slate-300 border border-transparent'
          ]"
        >
          <div class="min-w-0 flex-1 pr-2">
            <div class="flex items-center gap-1.5">
              <p class="text-xs font-bold truncate">{{ item.name }}</p>
              <Check v-if="item.id === activeTenant.id" class="w-3.5 h-3.5 text-sky-400 shrink-0" />
            </div>
            <p class="text-[10px] text-slate-400 mt-0.5">{{ item.seasonYear }}</p>
          </div>

          <span class="text-[9px] font-extrabold px-1.5 py-0.5 rounded-md uppercase border bg-slate-950 border-slate-800 shrink-0 text-slate-300">
            {{ getTierLabel(item.tier || item.type) }}
          </span>
        </button>
      </div>
    </div>

    <!-- Backdrop to close dropdown -->
    <div v-if="isOpen" class="fixed inset-0 z-40" @click="isOpen = false" />
  </div>
</template>
